import {
  DEFAULT_SHOPPING_PREFS,
  parseShoppingCheckId,
  syncPushResponseSchema,
  uuidv7,
  type CookSession,
  type ManualItem,
  type MealSlot,
  type PlanEntry,
  type ShoppingRecipe,
  type SyncOp,
  type SyncPushResponse,
} from '@dishly/shared';
import { api, ApiError, isNetworkError } from './api.ts';
import type { Mirror } from './mirror.ts';

/** An op waiting to reach the server; `seq` keeps the order they were made in. */
export interface QueuedOp {
  seq?: number;
  op: SyncOp;
}

/** What a caller hands in: the op minus what the queue stamps on it. */
export type NewOp = SyncOp extends infer O
  ? O extends SyncOp
    ? Omit<O, 'opId' | 'clientTs'>
    : never
  : never;

/**
 * Applies one op to the mirror the way the server will, so the plan and the list change at once,
 * offline too. Runs again on top of every pull until the server has the op (`rebaseOutbox`).
 */
export async function applyLocal(db: Mirror, op: SyncOp): Promise<void> {
  const updatedAt = op.clientTs;
  const remove = op.op === 'delete';
  switch (op.entity) {
    case 'cookSession': {
      if (remove) {
        await db.cookSessions.delete(op.id);
        await db.planEntries.where('cookSessionId').equals(op.id).delete();
        return;
      }
      const old = await db.cookSessions.get(op.id);
      const base = { doneAt: null, notes: null, imageId: null, createdBy: null };
      await db.cookSessions.put({
        ...base,
        ...old,
        ...op.fields,
        id: op.id,
        updatedAt,
      } as CookSession);
      return;
    }
    case 'planEntry': {
      if (remove) return db.planEntries.delete(op.id);
      const old = await db.planEntries.get(op.id);
      const base = { userId: null, cookSessionId: null, freeText: null, portions: 1 };
      await db.planEntries.put({
        ...base,
        ...old,
        ...op.fields,
        id: op.id,
        updatedAt,
      } as PlanEntry);
      return;
    }
    case 'mealSlot': {
      if (remove) return db.slots.delete(op.id);
      const old = await db.slots.get(op.id);
      const base = { label: null, defaultTime: null, visible: true };
      await db.slots.put({ ...base, ...old, ...op.fields, id: op.id, updatedAt } as MealSlot);
      return;
    }
    case 'shoppingCheck': {
      if (remove) return db.shoppingChecks.delete(op.id);
      const key = parseShoppingCheckId(op.id);
      if (!key) return;
      await db.shoppingChecks.put({
        id: op.id,
        ...key,
        state: op.fields.state ?? 'checked',
        checkedAt: updatedAt,
        checkedBy: null,
        updatedAt,
      });
      return;
    }
    case 'manualItem': {
      if (remove) return db.manualItems.delete(op.id);
      const old = await db.manualItems.get(op.id);
      const base = {
        quantity: null,
        unit: null,
        ingredientId: null,
        category: 'other',
        checkedAt: null,
        createdBy: null,
      };
      await db.manualItems.put({
        ...base,
        ...old,
        ...op.fields,
        id: op.id,
        updatedAt,
      } as ManualItem);
      return;
    }
    case 'shoppingRecipe': {
      if (remove) return db.shoppingRecipes.delete(op.id);
      const old = await db.shoppingRecipes.get(op.id);
      await db.shoppingRecipes.put({
        createdBy: null,
        ...old,
        ...op.fields,
        id: op.id,
        updatedAt,
      } as ShoppingRecipe);
      return;
    }
    case 'shoppingPrefs': {
      const old = await db.prefs.get('shopping');
      await db.prefs.put({
        ...DEFAULT_SHOPPING_PREFS,
        ...old,
        ...op.fields,
        key: 'shopping',
        updatedAt,
      });
      return;
    }
    case 'staple': {
      const entry = await db.ingredients.get(op.id);
      if (!entry) return;
      const { isStaple, runningLow } = op.fields;
      await db.ingredients.put({
        ...entry,
        household: {
          ...entry.household,
          ...(isStaple !== undefined && { isStaple: isStaple ?? entry.defaultStaple }),
          ...(runningLow !== undefined && { runningLow }),
        },
      });
      return;
    }
  }
}

/** Pending ops, oldest first. */
export async function pendingOps(db: Mirror): Promise<SyncOp[]> {
  return (await db.outbox.orderBy('seq').toArray()).map((q) => q.op);
}

/** Re-applies what the server doesn't have yet on top of freshly pulled rows. */
export async function rebaseOutbox(db: Mirror): Promise<void> {
  for (const op of await pendingOps(db)) await applyLocal(db, op);
}

/**
 * Queues ops (one user action can be several) and applies them to the mirror in one
 * transaction. Send them with `flushOutbox` (the sync loop does).
 */
export async function enqueue(db: Mirror, ops: NewOp[], now = new Date()): Promise<SyncOp[]> {
  const clientTs = now.toISOString();
  const stamped = ops.map((op) => ({ ...op, opId: uuidv7(), clientTs }));
  await db.transaction('rw', [...db.synced, db.outbox], async () => {
    for (const op of stamped) {
      await db.outbox.add({ op });
      await applyLocal(db, op);
    }
  });
  return stamped;
}

const BATCH = 100;

export type SendOps = (ops: SyncOp[]) => Promise<SyncPushResponse>;

const defaultSend: SendOps = async (ops) =>
  syncPushResponseSchema.parse(
    await api('/api/sync/push', { method: 'POST', body: JSON.stringify({ ops }) }),
  );

/**
 * Sends the queue in order, a batch at a time. The server answers every op (applied, duplicate,
 * stale or rejected), so each answered op leaves the queue; a stale or rejected one is corrected
 * by the next pull. Returns false when the server couldn't be reached (the ops stay queued).
 */
export async function flushOutbox(db: Mirror, send: SendOps = defaultSend): Promise<boolean> {
  for (;;) {
    const batch = await db.outbox.orderBy('seq').limit(BATCH).toArray();
    if (batch.length === 0) return true;
    let answered: string[];
    try {
      const { results } = await send(batch.map((q) => q.op));
      answered = results.map((r) => r.opId);
    } catch (err) {
      if (isNetworkError(err) || (err instanceof ApiError && err.status === 401)) return false;
      // The server refused the batch as a whole (a bug or an outdated client): drop it rather
      // than blocking every later change.
      if (err instanceof ApiError && err.status === 400) {
        console.warn('outbox batch refused', err.message);
        answered = batch.map((q) => q.op.opId);
      } else {
        return false;
      }
    }
    const done = new Set(answered);
    const keys = batch.filter((q) => done.has(q.op.opId)).map((q) => q.seq as number);
    if (keys.length === 0) return false;
    await db.outbox.bulkDelete(keys);
  }
}

/** Retry delay after `failures` failed flushes in a row: 2 s doubling, at most 5 minutes. */
export const backoffMs = (failures: number): number =>
  Math.min(2000 * 2 ** Math.max(0, failures - 1), 5 * 60_000);
